import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';
const PURPLE = '#8B5CF6';

const CARD: React.CSSProperties = {
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 20,
  padding: 28,
};

const INDUSTRIES = [
  'Select Industry',
  'Real Estate',
  'Restaurant',
  'Retail',
  'Healthcare',
  'Education',
  'Fitness',
  'Beauty & Salon',
  'Legal',
  'Finance',
  'Technology',
  'Other',
] as const;

const GOALS = ['Generate Leads', 'Sell Products', 'Show Portfolio', 'All of Above'] as const;

const STYLES = [
  { id: 'professional', label: '🏢 Professional' },
  { id: 'creative', label: '🎨 Creative' },
  { id: 'minimal', label: '🌿 Minimal' },
  { id: 'bold', label: '🔥 Bold' },
] as const;

const PAGE_OPTIONS = ['Home', 'About', 'Services', 'Contact', 'Blog', 'Gallery', 'Pricing', 'FAQ'] as const;

export function WebBuilderPage() {
  const [step, setStep] = useState(1);
  const [industry, setIndustry] = useState('');
  const [goal, setGoal] = useState('');
  const [style, setStyle] = useState('');
  const [pages, setPages] = useState<string[]>(['Home', 'About', 'Contact']);
  const [businessName, setBusinessName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [step1Error, setStep1Error] = useState(false);
  const [step2Error, setStep2Error] = useState(false);
  const [barKey, setBarKey] = useState(0);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<{ business: { business_name: string } | null }>('/business/me');
        if (data.business?.business_name) setBusinessName(data.business.business_name);
      } catch {
        /* demo */
      }
    })();
  }, []);

  useEffect(() => {
    if (!generating) return;
    const t = window.setTimeout(() => {
      setGenerated(true);
      setGenerating(false);
    }, 3000);
    return () => window.clearTimeout(t);
  }, [generating]);

  function togglePage(p: string) {
    if (p === 'Home' || p === 'Contact') return;
    setPages((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  }

  function startGenerate() {
    setBarKey((k) => k + 1);
    setGenerating(true);
  }

  const pill = (active: boolean): React.CSSProperties => ({
    border: `1px solid ${active ? ACCENT : 'var(--border)'}`,
    borderRadius: 999,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: active ? 'rgba(16,185,129,0.15)' : 'var(--card2)',
    color: active ? 'var(--text)' : 'var(--muted)',
  });

  const domainSlug = businessName.trim() ? businessName.trim().toLowerCase().replace(/\s+/g, '') : 'yourbrand';

  return (
    <div className="odc-page relative z-10" style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      <style>{`
        @keyframes odc-wb-fill {
          from { width: 0%; }
          to { width: 100%; }
        }
        .odc-wb-bar-inner {
          height: 100%;
          border-radius: 2px;
          background: linear-gradient(90deg, ${ACCENT}, #34d399);
          animation: odc-wb-fill 3s linear forwards;
        }
      `}</style>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              padding: '5px 14px',
              borderRadius: 999,
              border: '1px solid rgba(16,185,129,0.35)',
              background: 'rgba(16,185,129,0.08)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              color: ACCENT,
              textTransform: 'uppercase',
            }}
          >
            ODC WEBBUILDER · NEW
          </span>
          <h1 style={{ margin: '12px 0 0', fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800, color: 'var(--text)' }}>
            Build Your Website with AI
          </h1>
          <p style={{ margin: '12px 0 0', color: 'var(--muted)', fontSize: 15, maxWidth: 560, lineHeight: 1.6 }}>
            Answer 4 questions — AI builds your complete website in 2 minutes. Hosted, live &amp; SEO-optimized.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {['⚡ 2 Min Build', '🌐 Hosted Free', '🔍 SEO Ready'].map((t) => (
            <span
              key={t}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '8px 16px',
                background: 'var(--card)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div
        className="odc-wb-how"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}
      >
        {(
          [
            { icon: '📝', title: 'Describe', body: 'Answer 4 quick questions about your business' },
            { icon: '⚡', title: 'AI Builds', body: 'AI generates complete website with pages, content & SEO' },
            { icon: '🌐', title: 'Go Live', body: 'Connect your domain and publish instantly' },
          ] as const
        ).map((s) => (
          <div key={s.title} style={CARD}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{s.title}</h3>
            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--muted)', lineHeight: 1.55 }}>{s.body}</p>
          </div>
        ))}
      </div>
      <style>{`
        @media (max-width: 900px) {
          .odc-wb-how { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {!generated ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>
            {([1, 2, 3] as const).map((n) => {
              const done = step > n;
              const active = step === n;
              return (
                <React.Fragment key={n}>
                  {n > 1 ? (
                    <div
                      style={{
                        flex: '1 1 40px',
                        minWidth: 24,
                        maxWidth: 80,
                        height: 2,
                        background: step >= n ? ACCENT : 'var(--border)',
                        margin: '0 4px',
                      }}
                    />
                  ) : null}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 72 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: 14,
                        background: done ? 'rgba(16,185,129,0.2)' : active ? ACCENT : 'var(--card)',
                        border: active || done ? 'none' : '1px solid var(--border)',
                        color: done ? ACCENT : active ? '#fff' : 'var(--muted)',
                      }}
                    >
                      {done ? '✓' : n}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textAlign: 'center' }}>
                      {n === 1 ? 'Business' : n === 2 ? 'Goals' : 'Pages'}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          <div className="odc-card" style={{ ...CARD, maxWidth: 720, margin: '0 auto', width: '100%', transform: 'none' }}>
            {step === 1 ? (
              <>
                <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Tell us about your business</h2>
                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 8 }}>Business Name</span>
                  <input
                    className="odc-input"
                    style={{ width: '100%' }}
                    value={businessName}
                    onChange={(e) => {
                      setBusinessName(e.target.value);
                      setStep1Error(false);
                    }}
                    placeholder="e.g. ABC Real Estate"
                  />
                </label>
                <label style={{ display: 'block', marginBottom: 16 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', display: 'block', marginBottom: 8 }}>Industry</span>
                  <select
                    className="odc-select"
                    style={{ width: '100%' }}
                    value={industry}
                    onChange={(e) => {
                      setIndustry(e.target.value);
                      setStep1Error(false);
                    }}
                  >
                    {INDUSTRIES.map((opt) => (
                      <option key={opt} value={opt === 'Select Industry' ? '' : opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>
                {step1Error ? (
                  <p style={{ color: '#F87171', fontSize: 13, margin: '0 0 12px' }}>Please fill all fields</p>
                ) : null}
                <button
                  type="button"
                  className="odc-btn-primary"
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => {
                    if (businessName.trim() && industry) setStep(2);
                    else setStep1Error(true);
                  }}
                >
                  Next →
                </button>
              </>
            ) : null}

            {step === 2 ? (
              <>
                <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
                  What&apos;s your goal &amp; style?
                </h2>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>Website Goal</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                  {GOALS.map((g) => (
                    <button key={g} type="button" onClick={() => { setGoal(g); setStep2Error(false); }} style={pill(goal === g)}>
                      {g}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 8 }}>Visual Style</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {STYLES.map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => {
                        setStyle(st.id);
                        setStep2Error(false);
                      }}
                      style={pill(style === st.id)}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>
                {step2Error ? (
                  <p style={{ color: '#F87171', fontSize: 13, margin: '0 0 12px' }}>Please fill all fields</p>
                ) : null}
                <button
                  type="button"
                  className="odc-btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (goal && style) setStep(3);
                    else setStep2Error(true);
                  }}
                >
                  Next →
                </button>
              </>
            ) : null}

            {step === 3 ? (
              <>
                <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Which pages do you need?</h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 12,
                    marginBottom: 16,
                  }}
                  className="odc-wb-pages"
                >
                  {PAGE_OPTIONS.map((p) => {
                    const locked = p === 'Home' || p === 'Contact';
                    const on = pages.includes(p);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => !locked && togglePage(p)}
                        style={{
                          border: `1px solid ${on ? ACCENT : 'var(--border)'}`,
                          borderRadius: 12,
                          padding: '12px 16px',
                          textAlign: 'left',
                          cursor: locked ? 'default' : 'pointer',
                          background: on ? 'rgba(16,185,129,0.08)' : 'var(--card2)',
                          opacity: locked ? 0.95 : 1,
                        }}
                      >
                        <span style={{ fontWeight: 700, color: 'var(--text)' }}>{on ? '☑' : '☐'} {p}</span>
                        {locked ? (
                          <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Required</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
                <style>{`
                  @media (min-width: 640px) {
                    .odc-wb-pages { grid-template-columns: repeat(3, 1fr) !important; }
                  }
                `}</style>
                {generating ? (
                  <div style={{ width: '100%', height: 4, background: 'var(--border)', borderRadius: 2, marginBottom: 16, overflow: 'hidden' }}>
                    <div key={barKey} className="odc-wb-bar-inner" />
                  </div>
                ) : null}
                <button
                  type="button"
                  className="odc-btn-primary"
                  style={{ width: '100%', padding: '16px 24px', fontSize: 16 }}
                  disabled={generating}
                  onClick={startGenerate}
                >
                  {generating ? '✨ Building your website...' : '✨ Generate My Website'}
                </button>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <div className="odc-card" style={{ ...CARD, maxWidth: 720, margin: '0 auto', width: '100%', textAlign: 'center', transform: 'none' }}>
          <div style={{ fontSize: 64, lineHeight: 1 }}>🌐</div>
          <h2 style={{ margin: '16px 0 8px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Your Website is Ready!</h2>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>
            We&apos;ve generated a complete website for {businessName || 'your business'}
          </p>

          <div
            style={{
              marginTop: 24,
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid var(--border)',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: 'var(--bg2)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ display: 'flex', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B' }} />
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981' }} />
              </span>
              <div
                style={{
                  flex: 1,
                  fontSize: 11,
                  padding: '4px 10px',
                  borderRadius: 6,
                  background: 'var(--card)',
                  color: 'var(--muted)',
                  fontFamily: 'monospace',
                }}
              >
                https://{domainSlug}.odc.site
              </div>
            </div>
            <div
              style={{
                height: 300,
                background: 'linear-gradient(165deg, #020B18, #051525)',
                padding: 20,
                color: 'rgba(255,255,255,0.9)',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 8 }}>
                {businessName || 'Your Brand'}
              </div>
              <p style={{ margin: '20px 0 8px', fontSize: 18, fontWeight: 800 }}>Welcome to {businessName || 'Your Brand'}</p>
              <p style={{ margin: 0, fontSize: 12, opacity: 0.75 }}>Premium experience · Trusted by customers</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 20 }}>
                {['Service A', 'Service B', 'Service C'].map((x) => (
                  <div
                    key={x}
                    style={{
                      borderRadius: 8,
                      padding: 12,
                      background: 'rgba(16,185,129,0.12)',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {x}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
            <button type="button" className="odc-btn-primary" disabled title="Coming soon">
              🌐 View Preview
            </button>
            <Link to="/settings" className="odc-btn-ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              🔗 Connect Domain
            </Link>
            <button
              type="button"
              className="odc-btn-ghost"
              onClick={() => {
                setGenerated(false);
                setStep(1);
              }}
            >
              ✏️ Edit &amp; Regenerate
            </button>
          </div>
          <p style={{ margin: '16px 0 0', fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
            Full website hosting coming in Pro plan. Currently generating preview.
          </p>
        </div>
      )}

      <div>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center' }}>
          Example Websites Built by ODC AI
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} className="odc-wb-samples">
          {(
            [
              {
                grad: 'linear-gradient(135deg, #020B18, #051525)',
                line: '🏠 ABC Real Estate | Premium Plots',
                badge: 'Real Estate',
                badgeColor: ACCENT,
                name: 'ABC Builders',
              },
              {
                grad: 'linear-gradient(135deg, #0A0A1A, #1A0A2E)',
                line: '🍽️ Spice Garden | Authentic Cuisine',
                badge: 'Restaurant',
                badgeColor: CYAN,
                name: 'Spice Garden',
              },
              {
                grad: 'linear-gradient(135deg, #0A1A0A, #0A2A1A)',
                line: '💅 Glow Studio | Beauty & Wellness',
                badge: 'Salon',
                badgeColor: PURPLE,
                name: 'Glow Studio',
              },
            ] as const
          ).map((s) => (
            <div key={s.name} className="odc-card" style={{ ...CARD, transform: 'none' }}>
              <div style={{ height: 160, borderRadius: 12, overflow: 'hidden', background: s.grad, padding: 16, color: '#fff', fontSize: 13, fontWeight: 700 }}>
                {s.line}
              </div>
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 12,
                  fontSize: 10,
                  fontWeight: 800,
                  padding: '4px 10px',
                  borderRadius: 8,
                  background: `${s.badgeColor}33`,
                  color: s.badgeColor,
                }}
              >
                {s.badge}
              </span>
              <p style={{ margin: '8px 0 0', fontWeight: 700, color: 'var(--text)' }}>{s.name}</p>
              <button type="button" className="odc-btn-ghost" style={{ marginTop: 12, fontSize: 12, padding: '8px 14px' }}>
                View Sample →
              </button>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .odc-wb-samples { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="odc-card" style={{ ...CARD, textAlign: 'center', transform: 'none' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>💎 WebBuilder Pricing</h3>
        <table className="odc-table" style={{ width: '100%', marginTop: 20, textAlign: 'left' }}>
          <tbody>
            <tr>
              <td style={{ fontWeight: 600 }}>Starter / Growth</td>
              <td>Preview only</td>
              <td>₹999/mo addon for full site</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Pro / Enterprise</td>
              <td>Included</td>
              <td>Full website + hosting</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Agency</td>
              <td>10 websites</td>
              <td>Included</td>
            </tr>
          </tbody>
        </table>
        <Link to="/pricing" className="odc-btn-ghost" style={{ marginTop: 20, display: 'inline-flex', textDecoration: 'none' }}>
          View All Plans →
        </Link>
      </div>
    </div>
  );
}
